/**
 * DOM scene reconciler — scaffolding-only adapter that consumes a
 * `SceneDiff` from `tento/tento-lona-js/sheets` and records the per-cell / per-row
 * operations a future DOM rewire would perform.
 *
 * Today, `DefaultBinderHelpers` open-codes its own walk: `onBind` →
 * `applyPlanFullRebind` / `applyPlanResize` → `StaticChildManager`
 * → legend creation. Migrating to consume `SceneDiff` is a multi-step
 * surgery on a hot path (cell pool, prosemirror editor lifecycle,
 * recycle hooks). This file lands the **contract** — `DomReconciler`
 * has the same diff-consuming shape as `CliReconciler` so:
 *
 *   1. Cross-platform parity tests can compare action traces.
 *   2. Future DOM rewire fills in `applyChange` bodies one branch at
 *      a time without changing the public surface.
 *
 * No DOM mutation happens here yet — `applyDiff` returns a typed
 * `DomActionLog` describing what the rewire would do. Tests can
 * assert the log's shape; production renders still go through
 * `DefaultBinderHelpers` until the rewire branch lands.
 */

import type {
  CellNode,
  ChangeKind,
  LegendNode,
  RowOverlayNode,
  SceneChange,
  SceneDiff,
  SceneKey,
} from "@tento-lona/sheets/platform";
import type { CommitBundle } from "./commit-bundle.ts";
import { decideSkip, type SkipEligibility } from "./skip-eligibility.ts";

/** One recorded operation the reconciler would perform against the DOM. */
export type DomAction =
  | {
    readonly tag: "cell-insert";
    readonly key: SceneKey;
    readonly cell: CellNode;
  }
  | {
    readonly tag: "cell-update";
    readonly key: SceneKey;
    readonly cell: CellNode;
    readonly what: ChangeKind;
  }
  | {
    readonly tag: "cell-remove";
    readonly key: SceneKey;
  }
  | {
    readonly tag: "overlay-insert";
    readonly key: SceneKey;
    readonly overlay: RowOverlayNode;
  }
  | {
    readonly tag: "overlay-update";
    readonly key: SceneKey;
    readonly overlay: RowOverlayNode;
    readonly what: ChangeKind;
  }
  | {
    readonly tag: "overlay-remove";
    readonly key: SceneKey;
  }
  | {
    readonly tag: "legend-insert";
    readonly key: SceneKey;
    readonly legend: LegendNode;
  }
  | {
    readonly tag: "legend-update";
    readonly key: SceneKey;
    readonly legend: LegendNode;
    readonly what: ChangeKind;
  }
  | {
    readonly tag: "legend-remove";
    readonly key: SceneKey;
  };

export type DomActionLog = readonly DomAction[];

/**
 * The complete effect surface a `CommitBundle` is applied against — every
 * structural / dispatch DECISION lives above it (in the compiled bundle) and
 * every EFFECT lives below it (in the binder's host). The reconciler's walk
 * (`applyStructure` / `applyCells`) is therefore a decision-free switch over op
 * tags: it never branches on plan shape, only routes tags to host methods.
 *
 * `Cell` is generic so the walk never names a DOM type (keeping this file
 * loadable under Deno alongside the pure `commit-bundle`) and so a pure model
 * host can stand in for parity tests. `occupantAt` resolves the LIVE cell at a
 * slot at DRAIN TIME (learning 2026-06-18: bind the drain-time occupant, never
 * precompute) — the bundle carries only slots + keys, never a cell reference.
 */
export interface CommitHost<Cell> {
  /** The live cell currently occupying `slot`, resolved at apply time. */
  occupantAt(slot: number): Cell;
  /** Splice a fresh cell in at `slot` (`mount` op). */
  mount(slot: number, key: string): void;
  /** Reuse the cell at `slot` in place; MUST fire `notifyCellRecycled`. */
  recycle(slot: number, key: string): void;
  /**
   * Remove the cell at `slot`: sync `--height:0` OUTSIDE the mutate (the
   * phantom-gap guard), `removeFromDom` INSIDE, then splice it out.
   */
  remove(slot: number): void;
  /** The single `$$.mutate` header/body/footer partition + reconcileChildren. */
  reconcile(): void;
  /** Mark a mounted/recycled row new (previousRenderState + renderCursor.evict). */
  markNew(key: string): void;
  /** Full per-cell rebind of the target cell at `rowIndex` (→ applyPlanFullRebind). */
  rebind(cell: Cell, rowIndex: number): void;
  /**
   * Re-flow the target cell at `rowIndex` WITHOUT rebuilding content — layout /
   * span / ARIA-rowindex only (→ applyPlanReflow). Invoked for `resize` / `span`
   * directives when the caller honors the classified cells.
   */
  reflow(cell: Cell, rowIndex: number): void;
  /**
   * The three skip terms for the cell at `rowIndex`, gathered by the host: the
   * scene's verdict, the ledger's verdict, and the three out-of-band DOM axes
   * the host evaluates ONCE per cell per pass. The host gathers; `decideSkip`
   * decides. This replaced a `canSkip(rowIndex): boolean` that made the same
   * conjunction itself — one of the four places the question was once
   * answered.
   */
  eligibility(rowIndex: number): SkipEligibility;
}

/**
 * DOM reconciler. Two surfaces:
 *
 *  1. `applyStructure` / `applyCells` — the LIVE commit applier: decision-free
 *     walks that execute a compiled `CommitBundle` against a `CommitHost`,
 *     byte-identical to the fused inline `onBind` loop they replace.
 *  2. `applyDiff` — scaffolding that maps a `SceneDiff` to a typed action log
 *     (the SceneDiff-driven structure rewire is a later slice); it writes
 *     nothing and stays the cross-platform parity record surface.
 */
export class DomReconciler {
  /**
   * Apply a `CommitBundle`'s STRUCTURE section against a host: the mutating ops
   * (mount / recycle / remove) in changeset order — reproducing the inline
   * `onBind` structural loop's splice sequence exactly — then the single
   * `reconcile()` partition, then the markNew pass over the mount + recycle ops.
   * `keep` ops are inert. No decision here: each op maps 1:1 to a host effect.
   */
  applyStructure<Cell>(bundle: CommitBundle, host: CommitHost<Cell>): void {
    for (const op of bundle.structure) {
      switch (op.tag) {
        case "mount":
          host.mount(op.slot, op.key);
          break;
        case "recycle":
          host.recycle(op.slot, op.key);
          break;
        case "remove":
          host.remove(op.slot);
          break;
        case "keep":
          break;
      }
    }
    host.reconcile();
    for (const op of bundle.structure) {
      if (op.tag === "mount" || op.tag === "recycle") host.markNew(op.key);
    }
  }

  /**
   * Apply a `CommitBundle`'s CELLS section: one directive per non-removed target
   * cell, in target order. The directive index IS the target slot / rowIndex
   * (cells are target-ordered and 1:1 with rows / plans), so the occupant is
   * resolved at that slot at drain time.
   *
   * When `honor` is `false` EVERY cell takes the full `rebind` path regardless of
   * its directive tag — byte-identical to the all-`rebind` structure-only applier (the
   * tags are inert). When `honor` is `true` the applier routes each tag:
   * `rebind`→`rebind`, `resize`/`span`→`reflow`, and `skip`→ no-op when
   * `decideSkip(host.eligibility(i))` says so, else a fallback `rebind`.
   * Decision-free either way: the walk still never inspects plan shape or
   * scene — it hands the host's gathered eligibility to the one decider
   * and routes the verdict.
   */
  applyCells<Cell>(bundle: CommitBundle, host: CommitHost<Cell>, honor: boolean): void {
    for (const [i, directive] of bundle.cells.entries()) {
      if (!honor) {
        host.rebind(host.occupantAt(i), i);
        continue;
      }
      switch (directive.tag) {
        case "rebind":
          host.rebind(host.occupantAt(i), i);
          break;
        case "resize":
        case "span":
          host.reflow(host.occupantAt(i), i);
          break;
        case "skip":
          // The ONE decider. The host gathered the three terms; `decideSkip`
          // is the only thing that turns them into an answer.
          if (decideSkip(host.eligibility(i)).kind === "rebind") {
            host.rebind(host.occupantAt(i), i);
          }
          break;
      }
    }
  }

  applyDiff(diff: SceneDiff): DomActionLog {
    const actions: DomAction[] = [];
    for (const change of diff.cells) actions.push(mapCellChange(change));
    for (const change of diff.rowOverlays) {
      actions.push(mapOverlayChange(change));
    }
    for (const change of diff.legends) actions.push(mapLegendChange(change));
    return actions;
  }
}

function mapCellChange(change: SceneChange<CellNode>): DomAction {
  switch (change.tag) {
    case "insert":
      return { tag: "cell-insert", key: change.key, cell: change.next };
    case "update":
      return {
        tag: "cell-update",
        key: change.key,
        cell: change.next,
        what: change.what,
      };
    case "remove":
      return { tag: "cell-remove", key: change.key };
  }
}

function mapOverlayChange(change: SceneChange<RowOverlayNode>): DomAction {
  switch (change.tag) {
    case "insert":
      return { tag: "overlay-insert", key: change.key, overlay: change.next };
    case "update":
      return {
        tag: "overlay-update",
        key: change.key,
        overlay: change.next,
        what: change.what,
      };
    case "remove":
      return { tag: "overlay-remove", key: change.key };
  }
}

function mapLegendChange(change: SceneChange<LegendNode>): DomAction {
  switch (change.tag) {
    case "insert":
      return { tag: "legend-insert", key: change.key, legend: change.next };
    case "update":
      return {
        tag: "legend-update",
        key: change.key,
        legend: change.next,
        what: change.what,
      };
    case "remove":
      return { tag: "legend-remove", key: change.key };
  }
}
